import { useState } from "react"
import PropTypes from "prop-types"
import { isEmpty } from "lodash"
import { Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions, Button, IconButton, Alert } from "@mui/material"
import { Close } from "@mui/icons-material"
import "./styles.scss"

export default function FeedbackForm({
  open,
  onClose,
  feedbackTrackingProps,
  onAdditionalFeedback,
  title = "Help us improve",
  contentText = "Please share your feedback below. (optional)",
  successMessage = "Thank you for your feedback!",
  getPlaceholderText,
}) {
  const [feedback, setFeedback] = useState("")
  const [showFeedbackSubmitted, setShowFeedbackSubmitted] = useState(false)
  const { direction } = feedbackTrackingProps

  // Default placeholder text generator
  const getDefaultPlaceholderText = (direction) =>
    `Why was this ${direction === "positive" ? "helpful" : "unhelpful"}?`

  const placeholder = getPlaceholderText
    ? getPlaceholderText(direction)
    : getDefaultPlaceholderText(direction)

  function handleClose() {
    setFeedback("")
    setShowFeedbackSubmitted(false)
    onClose()
  }

  function handleSubmit(event) {
    event.preventDefault()

    onAdditionalFeedback?.(feedback)
    setShowFeedbackSubmitted(true)

    // Auto-close after showing success message
    setTimeout(() => {
      handleClose()
    }, 2000)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      className="feedback-form-dialog"
      disableRestoreFocus
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit,
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <IconButton id="close-feedback-form" onClick={handleClose}>
        <Close />
      </IconButton>
      <DialogContent>
        {showFeedbackSubmitted ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        ) : (
          <>
            <DialogContentText>
              {contentText}
            </DialogContentText>
            <TextField
              id="feedback-form-textarea"
              variant="outlined"
              value={feedback}
              autoFocus
              margin="dense"
              multiline
              minRows={2}
              name="feedback"
              type="text"
              fullWidth
              placeholder={placeholder}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      {!showFeedbackSubmitted && (
        <DialogActions>
          <Button color="secondary" onClick={handleClose}>Cancel</Button>
          <Button color="primary" type="submit" disabled={isEmpty(feedback)}>Submit</Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

FeedbackForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  feedbackTrackingProps: PropTypes.shape({
    direction: PropTypes.oneOf(["positive", "negative"]),
  }).isRequired,
  title: PropTypes.string,
  contentText: PropTypes.string,
  successMessage: PropTypes.string,
  getPlaceholderText: PropTypes.func, // Function that takes direction and returns placeholder text
  onAdditionalFeedback: PropTypes.func,
}
